/**
 * Settings > Report Configuration > Reporting Lock (admin only).
 *
 * Configures, per center, how far one open report reaches — i.e. what else gets
 * blocked (and shows the live "R" badge) while a radiologist is reporting:
 *
 *   This study only                        the accession being reported   (default)
 *   All studies of the same order number   sibling accessions of one billed order
 *   All studies of the patient in this      every study of that patient in the center
 *   center
 *
 * Per center rather than one global switch because centers run independently:
 * a busy center may want the patient locked while a small one does not.
 * A center left on the default behaves exactly as the system did before this
 * feature existed, so nothing starts blocking silently.
 *
 * Scope options come from the backend (/reporting-lock-config) rather than being
 * hard-coded here, so the enum cannot drift from reporting-lock-helper.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Select, Table, Tag, message } from 'antd';
import { InfoCircleOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { makeGetCall, makePostCall } from '../../utils/helper';
import { useSites } from '../../hooks/useSites';
import '../MastersConfig/masters-config.css';

interface ScopeOption {
  value: string;
  label: string;
  description: string;
}

const SCOPE_TAG_COLOR: Record<string, string> = {
  ORDER: 'default',
  ORDER_GROUP: 'gold',
  PATIENT_CENTER: 'purple',
};

const ReportingLockConfig = () => {
  const { centers, loading: centersLoading } = useSites();

  const [scopes, setScopes] = useState<ScopeOption[]>([]);
  const [defaultScope, setDefaultScope] = useState('ORDER');
  // center code -> scope, the working copy the admin is editing
  const [byCenter, setByCenter] = useState<Record<string, string>>({});
  const [savedByCenter, setSavedByCenter] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await makeGetCall('/reporting-lock-config');
      if (!res?.data?.success) throw new Error(res?.data?.message || 'Failed to load configuration');

      const stored: Record<string, string> = {};
      Object.entries(res.data.byCenter || {}).forEach(([code, cfg]: [string, any]) => {
        const scope = typeof cfg === 'string' ? cfg : cfg?.scope;
        if (scope) stored[code] = scope;
      });

      setScopes(res.data.scopes || []);
      setDefaultScope(res.data.defaultScope || 'ORDER');
      setByCenter(stored);
      setSavedByCenter(stored);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e.message || 'Failed to load reporting lock configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const scopeFor = (code: string) => byCenter[code] || defaultScope;

  const isDirty = useMemo(() => {
    const keys = new Set([...Object.keys(byCenter), ...Object.keys(savedByCenter)]);
    return [...keys].some(k => (byCenter[k] || defaultScope) !== (savedByCenter[k] || defaultScope));
  }, [byCenter, savedByCenter, defaultScope]);

  const handleScopeChange = (code: string, scope: string) => {
    setByCenter(prev => {
      const next = { ...prev };
      // The default is the absence of an entry, so don't store it — that keeps
      // "not configured" and "explicitly set to the default" the same thing.
      if (scope === defaultScope) delete next[code];
      else next[code] = scope;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, { scope: string }> = {};
      Object.entries(byCenter).forEach(([code, scope]) => { payload[code] = { scope }; });

      const res = await makePostCall('/reporting-lock-config', { byCenter: payload });
      if (!res?.data?.success) throw new Error(res?.data?.message || 'Save failed');

      message.success('Reporting lock configuration saved');
      setSavedByCenter(byCenter);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Center',
      dataIndex: 'label',
      key: 'center',
      width: 260,
      render: (label: string, rec: any) => (
        <span>
          {label} <span className="mc-muted">({rec.value})</span>
        </span>
      ),
    },
    {
      title: 'Lock scope',
      key: 'scope',
      width: 320,
      render: (_: any, rec: any) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={scopeFor(rec.value)}
          onChange={val => handleScopeChange(rec.value, val)}
          options={scopes.map(s => ({ value: s.value, label: s.label }))}
          optionRender={(opt: any) => {
            const scope = scopes.find(s => s.value === opt.value);
            return (
              <div>
                <div>{opt.label}</div>
                {scope?.description && (
                  <div style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'normal' }}>
                    {scope.description}
                  </div>
                )}
              </div>
            );
          }}
        />
      ),
    },
    {
      title: 'What gets blocked',
      key: 'description',
      render: (_: any, rec: any) => {
        const scope = scopes.find(s => s.value === scopeFor(rec.value));
        return <span className="mc-muted">{scope?.description || '-'}</span>;
      },
    },
    {
      title: '',
      key: 'state',
      width: 110,
      render: (_: any, rec: any) => {
        const scope = scopeFor(rec.value);
        return scope === defaultScope
          ? <Tag color="default">Default</Tag>
          : <Tag color={SCOPE_TAG_COLOR[scope] || 'blue'}>Custom</Tag>;
      },
    },
  ];

  return (
    <div className="masters-config-container">
      <div className="mc-titlebar">
        <span className="mc-title">Reporting Lock</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchConfig} disabled={loading || saving}>
            Reload
          </Button>
          <Button
            type="primary"
            size="small"
            className="mc-add"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!isDirty}
            onClick={handleSave}
          >
            Save changes
          </Button>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          background: '#f8fafc',
          border: '1px solid #e3e6eb',
          borderRadius: 8,
          padding: '10px 12px',
          marginBottom: 14,
          fontSize: 12,
          color: '#475569',
        }}
      >
        <InfoCircleOutlined style={{ color: '#0f766e', marginTop: 2 }} />
        <div>
          While a radiologist has a study open in the report editor, that study shows a live
          <strong> R </strong> badge and no one else can open it. This setting widens that lock
          to the patient&apos;s other studies at the same center — they show an
          <strong> amber R </strong> and are blocked too, with a note saying who is reporting
          and which accession they are on. A user is never blocked by their own lock, and
          signed-off studies stay openable so old reports can still be read.
          <br />
          Centers left on <strong>{scopes.find(s => s.value === defaultScope)?.label || 'the default'}</strong>
          {' '}keep the original behaviour: only the study being reported is locked.
        </div>
      </div>

      <div className="mc-panel">
        <div className="mc-panel-head">
          <span>Centers</span>
          <span className="mc-count">{centers.length}</span>
        </div>
        <div className="mc-panel-body">
          <Table
            className="mc-table"
            size="small"
            rowKey="value"
            loading={loading || centersLoading}
            dataSource={centers}
            columns={columns}
            pagination={false}
          />
        </div>
      </div>
    </div>
  );
};

export default ReportingLockConfig;
